import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { AnalyzerDishForm } from './types';
import { AnalyzerDish, Dish } from 'types';
import { freqSplit } from 'utils/FreqUtils';
import DeleteConfirmationDialog from 'components/DeleteConfirmationDialog';
import { useForm } from 'react-hook-form';
import PairAnalyzerDishForm from './PairAnalyzerDishForm';
import { useDeleteAnalyzerDish } from './api/deleteAnalyzerDish';
import { useCreateAnalyzerDish } from './api/createAnalyzerDish';
import { useUpdateAnalyzerDish } from './api/updateAnalyzerDish';

interface Props {
  open: boolean;
  handleClose: () => void;
  analyzerDish: AnalyzerDish | Omit<AnalyzerDish, 'id'> | undefined;
  dish: Dish | undefined;
}

const initialFormState: AnalyzerDishForm = {
  id: null,
  port: '',
  feed_name: '',
  polarity: '',
  link_type: '',
  loValue: 0,
  loUnit: '',
  analyzer: '-1',
  switch: '-1',
  selectedEntity: 'analyzer',
};

const PairAnalyzerDishDialog = ({
  open,
  handleClose,
  analyzerDish,
  dish,
}: Props) => {
  const {
    control,
    setValue,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm<AnalyzerDishForm>({
    defaultValues: initialFormState,
  });
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const deleteConnection = useDeleteAnalyzerDish({});
  const createConnection = useCreateAnalyzerDish({});
  const updateConnection = useUpdateAnalyzerDish({});

  useEffect(() => {
    if (analyzerDish) {
      reset({
        ...analyzerDish,
        port: analyzerDish.port ? analyzerDish.port + '' : '',
        feed_name: analyzerDish.feed_name ? analyzerDish.feed_name : '',
        polarity: analyzerDish.polarity ? analyzerDish.polarity : '',
        link_type: analyzerDish.link_type ? analyzerDish.link_type : '',
        loValue:
          !analyzerDish.lo || isNaN(parseFloat(freqSplit(analyzerDish.lo)[0]))
            ? 0
            : Math.abs(parseFloat(freqSplit(analyzerDish.lo)[0])),
        loUnit:
          !analyzerDish.lo || !freqSplit(analyzerDish.lo)[1]
            ? 'MHz'
            : freqSplit(analyzerDish.lo)[1],
        analyzer: analyzerDish.analyzer_id
          ? '' + analyzerDish.analyzer_id
          : '-1',
        switch: analyzerDish.switch_id ? '' + analyzerDish.switch_id : '-1',
        selectedEntity: analyzerDish.analyzer_id ? 'analyzer' : 'switch',
      });
    } else {
      reset(initialFormState);
    }
  }, [reset, analyzerDish, dish, open]);

  const handleDelete = async () => {
    if (!analyzerDish || !('id' in analyzerDish) || !analyzerDish?.id) return;
    await deleteConnection.mutateAsync(analyzerDish.id);
    reset();
    handleClose();
  };

  const handleDeleteClicked = () => {
    setDeleteOpen(true);
  };

  const toggleEntity = () => {
    if (watch('selectedEntity') === 'analyzer') {
      setValue('selectedEntity', 'switch');
      setValue('analyzer', '-1');
    } else {
      setValue('selectedEntity', 'analyzer');
      setValue('switch', '-1');
    }
  };

  const onSubmit = async () => {
    if (!dish) return;
    const analyzerDishForm = getValues();
    const lo =
      analyzerDishForm.loValue && analyzerDishForm.loUnit
        ? analyzerDishForm.loValue + ' ' + analyzerDishForm.loUnit
        : undefined;
    if (!analyzerDishForm.id) {
      await createConnection.mutateAsync({
        ...analyzerDishForm,
        port: analyzerDishForm.port ? Number(analyzerDishForm.port) : undefined,
        analyzer_id:
          analyzerDishForm.analyzer && Number(analyzerDishForm.analyzer) !== -1
            ? Number(analyzerDishForm.analyzer)
            : null,
        switch_id:
          analyzerDishForm.switch && Number(analyzerDishForm.switch) !== -1
            ? Number(analyzerDishForm.switch)
            : null,
        dish_id: dish.id,
        polarity: analyzerDishForm.polarity
          ? analyzerDishForm.polarity
          : undefined,
        link_type: analyzerDishForm.link_type
          ? analyzerDishForm.link_type
          : undefined,
        lo,
      });
    } else {
      await updateConnection.mutateAsync({
        analyzerDishId: analyzerDishForm.id,
        data: {
          ...analyzerDishForm,
          port: analyzerDishForm.port ? Number(analyzerDishForm.port) : null,
          analyzer_id:
            analyzerDishForm.analyzer &&
            Number(analyzerDishForm.analyzer) !== -1
              ? Number(analyzerDishForm.analyzer)
              : null,
          switch_id:
            analyzerDishForm.switch && Number(analyzerDishForm.switch) !== -1
              ? Number(analyzerDishForm.switch)
              : null,
          dish_id: dish.id,
          polarity: analyzerDishForm.polarity
            ? analyzerDishForm.polarity
            : null,
          link_type: analyzerDishForm.link_type
            ? analyzerDishForm.link_type
            : null,
          lo,
        },
      });
    }
    reset(initialFormState);
    handleClose();
  };

  const handleCancel = () => {
    reset(initialFormState);
    handleClose();
  };

  if (!dish) return null;
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {watch('id')
          ? 'Edit Connection For ' + dish.name
          : 'New Connection For ' + dish.name}
      </DialogTitle>
      <DialogContent>
        <PairAnalyzerDishForm
          watch={watch}
          control={control}
          errors={errors}
          toggleEntity={toggleEntity}
        />
      </DialogContent>
      <DialogActions>
        {analyzerDish && 'id' in analyzerDish && analyzerDish?.id && (
          <Button
            onClick={handleDeleteClicked}
            color='error'
            variant='contained'
          >
            Delete
          </Button>
        )}
        <Button onClick={handleCancel} color='secondary' variant='contained'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={() => handleSubmit(onSubmit)()}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
      <DeleteConfirmationDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onSubmit={handleDelete}
        itemToDelete={
          analyzerDish?.feed_name ? analyzerDish.feed_name : 'Connection'
        }
      />
    </Dialog>
  );
};

export default PairAnalyzerDishDialog;
